// Environmental context: real weather via Open-Meteo (keyless), full .epw parsing,
// and a deterministic mock fallback. Returns an enriched climate object:
//   { source, dli, temperature, humidity, ghi, dni, windSpeed, windDir, precip, gdd, frostFree }

const ARCHIVE_URL = "https://archive-api.open-meteo.com/v1/archive";

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}
function mean(a) {
  const f = a.filter((x) => typeof x === "number" && !isNaN(x));
  return f.length ? f.reduce((s, x) => s + x, 0) / f.length : 0;
}
function round(v, d = 1) {
  const m = Math.pow(10, d);
  return Math.round(v * m) / m;
}
// Circular mean for wind direction (degrees)
function circularMean(deg) {
  const f = deg.filter((x) => typeof x === "number" && !isNaN(x));
  if (!f.length) return 0;
  let sx = 0, sy = 0;
  f.forEach((d) => {
    const r = (d * Math.PI) / 180;
    sx += Math.cos(r);
    sy += Math.sin(r);
  });
  let a = (Math.atan2(sy, sx) * 180) / Math.PI;
  return (a + 360) % 360;
}
export function compassFromDeg(deg) {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(deg / 45) % 8];
}

/* ---------------- Seasonal bucketing (real data, keyed to solar geometry) ----------------
   Each day is assigned to a season by the sun's actual declination on that date, so the
   per-season DLI stays consistent with the sun-path model's SEASON_DECL (SUM = +23.44°,
   WNT = -23.44°). No invented seasonal curve — just averages of measured daily radiation. */
function dayOfYearISO(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  const start = Date.UTC(d.getUTCFullYear(), 0, 0);
  return Math.floor((d - start) / 86400000);
}
function declForDay(n) {
  return 23.44 * Math.sin((2 * Math.PI * (n - 81)) / 365);
}
function seasonKeyForDay(n) {
  const decl = declForDay(n);
  if (decl > 11.72) return "SUM";
  if (decl < -11.72) return "WNT";
  return Math.cos((2 * Math.PI * (n - 81)) / 365) > 0 ? "SPR" : "AUT"; // δ rising → spring side
}
// dliByDay: { [dayOfYear]: dliValue } → seasonal means, falling back to the annual value
// for any season with no samples so the heatmap always has a real number.
function seasonalDliFrom(dliByDay, fallback) {
  const buckets = { SPR: [], SUM: [], AUT: [], WNT: [] };
  Object.keys(dliByDay).forEach((day) => {
    const v = dliByDay[day];
    if (typeof v === "number" && !isNaN(v)) buckets[seasonKeyForDay(+day)].push(v);
  });
  const out = {};
  ["SPR", "SUM", "AUT", "WNT"].forEach((k) => {
    out[k] = round(clamp(buckets[k].length ? mean(buckets[k]) : fallback, 0, 60), 1);
  });
  return out;
}

/* ---------------- Open-Meteo (real, keyless) ---------------- */
// Uses the ERA5 archive over a full trailing year so DLI / GDD / temperature reflect an
// annual-average climate (season-independent) rather than the current 3-month window.
export async function fetchWeather({ lat, lng }) {
  const end = new Date();
  end.setDate(end.getDate() - 5); // ERA5 archive has a ~5-day lag
  const start = new Date(end);
  start.setFullYear(start.getFullYear() - 1);
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    start_date: isoDate(start),
    end_date: isoDate(end),
    hourly: "relative_humidity_2m,direct_normal_irradiance",
    daily:
      "temperature_2m_mean,temperature_2m_max,temperature_2m_min,shortwave_radiation_sum,precipitation_sum,wind_speed_10m_max,wind_direction_10m_dominant",
    timezone: "auto",
  });
  const res = await fetch(`${ARCHIVE_URL}?${params.toString()}`);
  if (!res.ok) throw new Error(`open-meteo ${res.status}`);
  const d = await res.json();
  const daily = d.daily || {};
  const hourly = d.hourly || {};

  const tmax = daily.temperature_2m_max || [];
  const tmin = daily.temperature_2m_min || [];
  const ndays = Math.max(1, (daily.time || []).length);

  const ghiMJ = mean(daily.shortwave_radiation_sum || []); // annual-mean MJ/m²/day
  const dli = ghiMJ * 2.06; // 1 MJ/m²·d ≈ 2.06 mol/m²·d PAR → annual-average DLI
  const dniVals = (hourly.direct_normal_irradiance || []).filter((x) => x > 0);

  // Growing Degree Days (base 10°C) — annualized
  let gddSum = 0, frostFreeDays = 0;
  for (let i = 0; i < ndays; i++) {
    const hi = tmax[i], lo = tmin[i];
    if (typeof hi === "number" && typeof lo === "number") {
      gddSum += Math.max(0, (hi + lo) / 2 - 10);
      if (lo > 0) frostFreeDays += 1;
    }
  }
  const gddAnnual = (gddSum / ndays) * 365;
  const frostFreeAnnual = Math.round((frostFreeDays / ndays) * 365);
  const precipAnnual = (mean(daily.precipitation_sum || []) || 0) * 365;

  // Per-season DLI from each day's measured shortwave radiation, bucketed by solar declination.
  const times = daily.time || [];
  const radDaily = daily.shortwave_radiation_sum || [];
  const dliByDay = {};
  for (let i = 0; i < times.length; i++) {
    if (typeof radDaily[i] === "number") dliByDay[dayOfYearISO(times[i])] = radDaily[i] * 2.06;
  }
  const seasonalDli = seasonalDliFrom(dliByDay, dli);

  return {
    source: "api",
    dli: round(clamp(dli, 0, 60), 1),
    seasonalDli,
    temperature: round(mean(daily.temperature_2m_mean || []), 1),
    humidity: Math.round(mean(hourly.relative_humidity_2m || [])),
    ghi: round(ghiMJ / 3.6, 2), // MJ/m²/day → kWh/m²/day
    dni: Math.round(mean(dniVals)), // W/m² (mean of daylight hours)
    windSpeed: round(mean(daily.wind_speed_10m_max || []), 1), // km/h
    windDir: Math.round(circularMean(daily.wind_direction_10m_dominant || [])),
    precip: Math.round(precipAnnual), // mm/yr
    gdd: Math.round(gddAnnual),
    frostFree: frostFreeAnnual, // days/yr
  };
}

/* ---------------- Deterministic mock (offline fallback) ---------------- */
export function mockWeather({ lat, lng }) {
  const absLat = Math.abs(lat);
  const dli = clamp(9 + (60 - absLat) * 0.42, 4, 42);
  const temperature = clamp(28 - absLat * 0.62, -12, 34);
  const humidity = clamp(58 + Math.sin((lng * Math.PI) / 180) * 18, 28, 92);
  const ghi = clamp(1.2 + (60 - absLat) * 0.06, 0.6, 7); // kWh/m²/day
  return {
    source: "estimate",
    dli: round(dli, 1),
    seasonalDli: { SPR: round(dli, 1), SUM: round(dli, 1), AUT: round(dli, 1), WNT: round(dli, 1) },
    temperature: round(temperature, 1),
    humidity: Math.round(humidity),
    ghi: round(ghi, 2),
    dni: Math.round(ghi * 130),
    windSpeed: round(8 + Math.abs(Math.sin(lat)) * 14, 1),
    windDir: Math.round(((lng % 360) + 360) % 360),
    precip: Math.round(clamp(1400 - absLat * 12, 150, 2200)),
    gdd: Math.round(clamp((temperature - 5) * 180, 0, 5000)),
    frostFree: Math.round(clamp(365 - absLat * 3.2, 30, 365)),
  };
}

/* ---------------- EPW parsing (real, client-side) — CLIMATE ONLY ---------------- */
export function parseEPW(text) {
  const lines = text.split(/\r?\n/);
  if (lines.length < 20) throw new Error("Not a valid .epw file");

  // Header LOCATION line: LOCATION,City,State,Country,Source,WMO,Lat,Lng,TZ,Elevation
  const header = (lines[0] || "").split(",");
  const stationLatRaw = parseFloat(header[6]);
  const stationLngRaw = parseFloat(header[7]);
  const stationName = [header[1], header[3]].map((s) => (s || "").trim()).filter(Boolean).join(", ");

  const data = lines.slice(8).filter((l) => l.split(",").length > 21);
  if (!data.length) throw new Error("No hourly data in .epw");

  const temps = [], rhs = [], ghis = [], dnis = [], wspd = [], wdir = [], precip = [];
  const dayMax = {}, dayMin = {}, dayGhi = {};
  data.forEach((l, i) => {
    const f = l.split(",");
    const t = parseFloat(f[6]);
    const rh = parseFloat(f[8]);
    const ghi = parseFloat(f[13]); // Wh/m²
    const dni = parseFloat(f[14]); // Wh/m²
    const wd = parseFloat(f[20]);
    const ws = parseFloat(f[21]); // m/s
    const pr = parseFloat(f[33]); // mm (liquid precip depth)
    if (!isNaN(t)) temps.push(t);
    if (!isNaN(rh)) rhs.push(rh);
    if (!isNaN(ghi)) ghis.push(ghi);
    if (!isNaN(dni) && dni > 0) dnis.push(dni);
    if (!isNaN(ws)) wspd.push(ws);
    if (!isNaN(wd)) wdir.push(wd);
    if (!isNaN(pr) && pr < 999) precip.push(pr);
    const day = Math.floor(i / 24);
    if (!isNaN(t)) {
      dayMax[day] = Math.max(dayMax[day] ?? -99, t);
      dayMin[day] = Math.min(dayMin[day] ?? 99, t);
    }
    if (!isNaN(ghi)) dayGhi[day] = (dayGhi[day] ?? 0) + ghi; // Wh/m² accumulated per day
  });

  const nHours = ghis.length || 1;
  const nDays = nHours / 24;
  const avgDailyGhiWh = ghis.reduce((a, b) => a + b, 0) / nDays; // Wh/m²/day
  const dli = avgDailyGhiWh * 0.0074;

  let gddSum = 0, frostFree = 0;
  const days = Object.keys(dayMax);
  days.forEach((d) => {
    gddSum += Math.max(0, (dayMax[d] + dayMin[d]) / 2 - 10);
    if (dayMin[d] > 0) frostFree += 1;
  });

  return {
    source: "epw",
    dli: round(clamp(dli, 0, 60), 1),
    seasonalDli: seasonalDliFrom(
      Object.fromEntries(Object.keys(dayGhi).map((d) => [+d + 1, dayGhi[d] * 0.0074])), // Wh/day → DLI, day index → day-of-year
      dli,
    ),
    temperature: round(mean(temps), 1),
    humidity: Math.round(mean(rhs)),
    ghi: round(avgDailyGhiWh / 1000, 2), // kWh/m²/day
    dni: Math.round(mean(dnis)),
    windSpeed: round(mean(wspd) * 3.6, 1), // m/s → km/h
    windDir: Math.round(circularMean(wdir)),
    precip: Math.round(precip.reduce((a, b) => a + b, 0)),
    gdd: Math.round(gddSum),
    frostFree,
    stationLat: isNaN(stationLatRaw) ? null : stationLatRaw,
    stationLng: isNaN(stationLngRaw) ? null : stationLngRaw,
    stationName: stationName || null,
  };
}

/* ---------------- Geo helpers ---------------- */
// Great-circle distance in km between two {lat,lng} points.
export function haversineKm(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(s)));
}
